// Release-quarantine modal (Story #4613, Stream 8c). A BESPOKE text-capture +
// confirm dialog (NOT ConfirmDialog — that has no text-input slot). A ≥20-char
// justification is required client-side too (defense in depth alongside the
// server-side check in support_core.RELEASE_JUSTIFICATION_MIN). The caller only
// renders this for innovateflow-security-admin users.

import { useState } from 'react'
import { Loader2, ShieldAlert, X } from 'lucide-react'
import { RELEASE_JUSTIFICATION_MIN } from '../../../constants/support'

interface ReleaseQuarantineModalProps {
  filename: string
  /** Whether a release is already in flight for this attachment (warn). */
  releaseInFlight?: boolean
  onConfirm: (justification: string) => Promise<void>
  onClose: () => void
}

export default function ReleaseQuarantineModal({
  filename,
  releaseInFlight,
  onConfirm,
  onClose,
}: ReleaseQuarantineModalProps) {
  const [justification, setJustification] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const remaining = RELEASE_JUSTIFICATION_MIN - justification.trim().length
  const tooShort = remaining > 0

  async function submit() {
    if (tooShort) return
    setBusy(true)
    setError(null)
    try {
      await onConfirm(justification.trim())
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Release failed — please try again')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Release quarantined attachment">
      <div className="absolute inset-0 bg-slate-900/50" onClick={() => !busy && onClose()} aria-hidden />
      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
            <ShieldAlert size={20} aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-iflow-900">Release quarantined file</h2>
            <p className="mt-1 text-sm text-slate-600">
              <span className="font-medium">{filename}</span> was flagged by the security scan. Releasing it makes it
              retrievable again. Record why this is safe — this justification is audited.
            </p>
          </div>
          <button type="button" onClick={() => !busy && onClose()} aria-label="Close" disabled={busy} className="text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>

        {releaseInFlight && (
          <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800">
            A release for this attachment is already in progress.
          </p>
        )}

        <label className="mt-4 block text-[12.5px] font-semibold text-iflow-900">
          Justification <span className="font-normal text-slate-500">(minimum {RELEASE_JUSTIFICATION_MIN} characters)</span>
        </label>
        <textarea
          rows={3}
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
          className="mt-1.5 w-full resize-y rounded-md border border-slate-300 px-2.5 py-2 text-[13px] text-slate-900"
          placeholder="e.g. Confirmed false positive — internal DWG export, verified against the source in the project."
        />
        <p className={`mt-1 text-[11.5px] ${tooShort ? 'text-slate-500' : 'text-emerald-600'}`}>
          {tooShort ? `${remaining} more character${remaining === 1 ? '' : 's'} required` : 'Justification meets the minimum length'}
        </p>

        {error && <p role="alert" className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={tooShort || busy}
            className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-amber-400 disabled:opacity-60"
          >
            {busy && <Loader2 size={15} className="animate-spin" />} Release file
          </button>
        </div>
      </div>
    </div>
  )
}
